import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { API_BASE } from '../config/api';
import AuthService from '../services/authService';
import { isMultiLocationDay, getDayLocations } from '../utils/dayLocations';
import * as ops from '../utils/tripDaysOps';
import useTripBoard from './useTripBoard';
import useTripAutosave from './useTripAutosave';
import { invalidatePlannerCache } from './plannerCache';
import { invalidateDashboardCache } from './useHomeDashboard';
import {
  namedRemovals, withServerIds, withClientIds, keepLocalIds, noteSentIds, learnInsertedIds, saveNeedsReload, dropActivities,
  mintActivityId
} from '../utils/plannerSaveDiff';
import { EDIT_ORIGIN } from '../utils/editOrigin';
import { tripEditRights, CO_ORGANIZER_ROLES } from '../utils/tripEditRights';
import { withOpIdentity, stopKey as opStopKey, mintOpId } from '../utils/tripActivityOps';
import { walksInStep } from '../utils/dayWalks';
import { walkPlaceItems, defaultWalkPlaces, walkWithPlaces } from '../utils/walkAdd';
import DiscoveryService from '../services/discoveryService';
import { withHereClearedOnRetime } from '../utils/quickStops';
import { planItineraryOps, replayItinerary, dayUidsNamed } from '../utils/tripPlanOps';
import { formatDateWithWeekdayShort } from '../utils/dateUtils';
import useTripOpsSync from './useTripOpsSync';
import notificationsServiceDB from '../services/notificationsServiceDB';

// Module-level cache so Trip view data survives page navigations
// (e.g. Home → Trip → Home → Trip renders instantly within TTL).
// Key: `${userId}:${tripId}` → { data, ts }
const tripDetailCache = new Map();
const TRIP_DETAIL_TTL_MS = 60_000;

const getTripCached = (userId, tripId) => {
  if (!userId || !tripId) return null;
  const entry = tripDetailCache.get(`${userId}:${tripId}`);
  if (!entry) return null;
  if (Date.now() - entry.ts > TRIP_DETAIL_TTL_MS) {
    tripDetailCache.delete(`${userId}:${tripId}`);
    return null;
  }
  return entry.data;
};

const setTripCached = (userId, tripId, data) => {
  if (!userId || !tripId) return;
  tripDetailCache.set(`${userId}:${tripId}`, { data, ts: Date.now() });
};

// Invalidates all cached entries for a trip (any viewer). Exported so
// components that mutate trip data outside of save/autosave (e.g. booking
// URL edits, traveler add/remove) can force a refetch on next mount.
export const invalidateTripCache = (tripId) => {
  if (!tripId) return;
  const suffix = `:${tripId}`;
  for (const key of tripDetailCache.keys()) {
    if (key.endsWith(suffix)) tripDetailCache.delete(key);
  }
};

// Clear every cached trip for every viewer — auth-boundary use only
// (invalidateSessionCaches), so one account's trip data can't survive into
// the next session on the same device.
export const clearTripCaches = () => {
  tripDetailCache.clear();
};

/**
 * Consolidated state management hook for TripPlannerPage.
 * Manages trip data, form state, permissions, CRUD operations, and trip day manipulation.
 * Includes AbortController cleanup for all fetch operations.
 *
 * @param {string|undefined} tripId - Trip ID from URL params (undefined for new trips)
 * @param {object} user - Current authenticated user
 * @param {URLSearchParams} searchParams - URL search params for new trip initialization
 */
// Strip base64 photo blobs from tripDays before saving.
// The server re-resolves photos from user_venue_photos on every GET,
// so sending them back in the PUT body is pure dead weight (~4MB+).
function stripPhotosForSave(tripDays) {
  return tripDays.map(day => ({
    ...day,
    activities: (day.activities || []).map(({ image, coverPhoto, cover_photo, ...rest }) => rest)
  }));
}

// Places this session has already asked the server to name (see the
// refresh effect in useTripState). Module scope so a re-mount doesn't re-ask.
const refreshAttemptedPlaceIds = new Set();
// The server names at most 20 places per request (placeBasicsRefresh).
const PLACE_REFRESH_BATCH = 20;
// Mirrors the server's isValidPlaceId (placesRoutes.js): our own id
// namespaces look like Google's but must never reach a paid lookup.
const INTERNAL_ID_PREFIXES = ['atr_', 'act_', 'tm_', 'event_', 'custom_', 'template_'];
/** Two chip lists the same (the Trip setup place chips, as loaded or as edited). */
const sameDestinations = (a, b) => JSON.stringify(a || []) === JSON.stringify(b || []);

function looksLikeGooglePlaceId(placeId) {
  return typeof placeId === 'string'
    && placeId.length >= 20
    && /^[A-Za-z0-9_-]+$/.test(placeId)
    && !INTERNAL_ID_PREFIXES.some(prefix => placeId.startsWith(prefix));
}

/**
 * The ids every reloadTrip request up to and including `seq` was asked to
 * merge, removed from `pending` (seq → { addedActivityIds, refreshedPlaceIds }).
 * Later requests keep theirs: this response may predate the stops they name.
 */
export function takePendingReloadIds(pending, seq) {
  const added = new Set();
  const refreshed = new Set();
  [...pending.keys()].filter(key => key <= seq).forEach(key => {
    const ids = pending.get(key);
    (ids.addedActivityIds || []).forEach(id => added.add(id));
    (ids.refreshedPlaceIds || []).forEach(id => refreshed.add(id));
    pending.delete(key);
  });
  return { addedActivityIds: [...added], refreshedPlaceIds: [...refreshed] };
}

// How long a saved walk added to a day waits for its places (addCuratedWalk).
const WALK_READ_MS = 5000;

/**
 * Whether reloadTrip may replace the itinerary with the server's copy: the
 * local itinerary is the saved one, AND no save landed while the GET was in
 * flight. A save that lands then moves the baseline, and the GET may have read
 * the rows before it committed — installing that copy would undo the saved
 * edit locally, and the next autosave (a full replace) would undo it on the
 * server too.
 */
export function reloadIsClean({ savedAtStart, saved, tripDays, transports }) {
  if (saved !== savedAtStart) return false;
  if (!saved) return true;
  return tripDays === saved.tripDays && transports === saved.transports;
}

/**
 * Google place ids of the stops that came back with no place data at all —
 * no Google row, none of our own, no linked landmark: the server shapes them
 * like custom items (source 'custom'), and anything named by Google or a
 * landmark comes back as source 'place'. A stop that carries its own title
 * (a user renamed it before source-named stops were locked) is not one:
 * we hold a name, and naming it from Google would replace the user's title.
 */
export function unnamedGooglePlaceIds(tripDays) {
  const ids = new Set();
  (tripDays || []).forEach(day => (day.activities || []).forEach(a => {
    const placeId = a?.placeId || a?.place_id;
    if (!placeId || a.source === 'place' || !looksLikeGooglePlaceId(placeId)) return;
    if (a.title || a.name) return;
    ids.add(placeId);
  }));
  return [...ids];
}

function useTripState(tripId, user, searchParams, { opsEnabled = false } = {}) {
  const isNewTrip = !tripId || tripId === 'new';
  const isViewMode = searchParams?.get('mode') === 'view';

  // Core trip state
  const [trip, setTrip] = useState(null);
  const [tripDays, setTripDays] = useState([]);
  // Transportation segments (flights, trains, …). Trip-level, NOT nested in
  // tripDays — a segment carries its own departure/arrival datetimes and the
  // UI associates it with a day by departure date, so date shifts and day
  // deletions can never orphan or wipe one.
  const [transports, setTransports] = useState([]);
  // Mirrors for reloadTrip, which decides after an await whether the
  // itinerary has unsaved edits.
  const tripDaysRef = useRef(tripDays);
  tripDaysRef.current = tripDays;
  const transportsRef = useRef(transports);
  transportsRef.current = transports;
  const currentTripIdRef = useRef(tripId);
  currentTripIdRef.current = tripId;
  const [loading, setLoading] = useState(!isNewTrip);
  const [error, setError] = useState(null);

  // Edit mode
  const [isEditMode, setIsEditMode] = useState(!isViewMode);
  const [isSaving, setIsSaving] = useState(false);

  // Form state (consolidated)
  const [tripName, setTripName] = useState('');
  const [tripLocation, setTripLocation] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [tripType, setTripType] = useState('leisure');
  const [description, setDescription] = useState('');
  const [selectedFriends, setSelectedFriends] = useState([]);
  const [destinations, setDestinationsState] = useState([]);
  const destinationsRef = useRef(destinations);
  destinationsRef.current = destinations;
  const [itineraryViewPreference, setItineraryViewPreference] = useState('high-level');

  // Permissions (consolidated)
  const [canEdit, setCanEdit] = useState(true);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [tripOwnerId, setTripOwnerId] = useState(null);
  // The rights the planner last applied, for a live re-read to compare
  // against (someone changed this person's edit rights — see reloadTrip).
  const rightsRef = useRef(null); // { canEdit, role } | null
  // reloadTrip, for fetchTrip (declared above it) to re-read after a cached
  // mount.
  const reloadTripRef = useRef(null);
  // Tells the page this person's rights just changed under an open planner
  // ({ kind: 'lost' | 'gained' | 'coOrganizer' | 'notCoOrganizer' }), for a
  // toast. Cleared by the page.
  const [accessNotice, setAccessNotice] = useState(null);
  const clearAccessNotice = useCallback(() => setAccessNotice(null), []);
  // Loaded by someone not (yet) on the trip — a pending invitee, or anyone
  // the trip's visibility let in. The server leaves another person's limited
  // activities out of their copy (C53), so it must never become the base of
  // an autosave once they join (C114): an answered invite refetches it.
  const loadedAsNonMemberRef = useRef(false);
  // Counts answered invitations, so a load that was in flight when one was
  // answered knows its copy may predate the answer.
  const inviteAnswersRef = useRef(0);
  // The silent refetch the invites effect below installs; fetchTrip calls it
  // when its own load raced an answer.
  const refetchAfterJoinRef = useRef(null);
  // Read through a ref so applyTripData (and with it fetchTrip, whose identity
  // re-runs the mount fetch) does not change when ?mode= does.
  const isViewModeRef = useRef(isViewMode);
  isViewModeRef.current = isViewMode;

  // On the trip (the owner or an accepted traveller), read from the trip GET's
  // own copy as tripEditRights reads it. The members-only calls (the board,
  // the chat's unread count, the change log, presence) wait for it: anyone
  // else is refused them (C368(8)).
  const isTripMember = useMemo(
    () => tripEditRights({ user_id: tripOwnerId, travelers: selectedFriends }, user?.id).isMember,
    [tripOwnerId, selectedFriends, user?.id]
  );
  // The viewer is a child on the trip: a `child_` id (the server's own test,
  // tripExpensesService's member set) or a roster row typed child. The Costs
  // tab is adults only and asks nothing for them (C387(1)).
  const isChildTraveler = useMemo(() => {
    if (!user?.id) return false;
    if (String(user.id).startsWith('child_')) return true;
    const me = (selectedFriends || []).find(trav => (trav.user_id || trav.id) === user.id);
    return !!me && (me.traveler_type === 'child' || me.type === 'child');
  }, [selectedFriends, user?.id]);

  // Trip board state (fetch, collections list, creation) lives in its own
  // hook — its only coupling back into trip state is tripName.
  const { tripBoard, boardCollections, isCreatingBoard, fetchTripBoard, handleCreateTripBoard } =
    useTripBoard(tripId, isNewTrip, tripName, isTripMember);

  // Walking tour stop map
  const [walkingTourStopMap, setWalkingTourStopMap] = useState({});

  const prevTripLocationRef = useRef(tripLocation);

  // O(1) itinerary change detection by array identity: dirty means the
  // current tripDays/transports arrays are not the ones last loaded or saved.
  // Real mutations always produce a new array and no-op updaters return prev
  // unchanged, so identity is exactly "content changed". This replaces a
  // version counter bumped inside the setState updaters — React may invoke
  // updaters more than once (StrictMode, interrupted renders), so that bump
  // made the updaters impure and could double-count, stranding
  // hasUnsavedChanges on. (The counter itself replaced JSON.stringify
  // deep-diffs that ran on every metadata keystroke.)
  // null means the initial load hasn't completed (autosave must not fire
  // yet); applyTripData baselines it with the loaded arrays.
  const savedItineraryRef = useRef(null); // { tripDays, transports } | null
  // Numbers reloadTrip's requests so an older response can't land after a
  // newer one has applied. Each request's own ids wait in reloadPendingRef
  // until a response at or after it applies and takes them, so dropping an
  // older response never drops the stops it was asked to merge.
  const reloadSeqRef = useRef(0);
  const reloadAppliedSeqRef = useRef(0);
  const reloadPendingRef = useRef(new Map());

  // Named removals (plannerSaveDiff): a save deletes only what this planner
  // names, so someone else's new stop survives it. serverIdsRef maps a new
  // stop's minted id (clientActivityId) to the row a save stored it as;
  // mergedIdsRef holds stops a re-read merged into unsaved edits and
  // sentIdsRef new stops a save carried (shown, not yet in the baseline —
  // the second until a save's answer arrives); reloadCueSeenRef the
  // kept/gone ids a save already re-read for.
  const serverIdsRef = useRef(new Map());
  const mergedIdsRef = useRef(new Set());
  const sentIdsRef = useRef(new Set());
  const reloadCueSeenRef = useRef(new Set());
  // The trip revision this planner's itinerary reflects (trips.revision, from
  // its last full read, or a save that started from it). A trip_changed at
  // or below it carries nothing new, so it skips the re-read.
  const knownRevisionRef = useRef(null);

  // Ops mode (trip co-editing, src/config/tripOps.js): every itinerary edit
  // goes to POST /:id/ops as ops (useTripOpsSync) — stops (Stage C), days,
  // stays, transports and trip dates (Stage D), the Trip setup place chips
  // (D11) — and no whole-itinerary save is sent. What the planner shows is
  // the server's copy (confirmedRef: days, Unscheduled stops, transports,
  // destinations) with the pending ops replayed on top.
  const [opsMode, setOpsMode] = useState(false);
  const opsModeRef = useRef(false);
  opsModeRef.current = opsMode;
  const confirmedRef = useRef({ days: [], unscheduled: [], transports: [], destinations: [] });
  // Unscheduled (D5): stops taken off a removed day, on no day until put back.
  const [unscheduled, setUnscheduled] = useState([]);
  const unscheduledRef = useRef(unscheduled);
  unscheduledRef.current = unscheduled;

  useEffect(() => {
    knownRevisionRef.current = null;
    // Another trip's rights are no baseline for this one's first re-read.
    rightsRef.current = null;
    serverIdsRef.current = new Map();
    mergedIdsRef.current = new Set();
    sentIdsRef.current = new Set();
    reloadCueSeenRef.current = new Set();
    setOpsMode(false);
    opsModeRef.current = false;
    confirmedRef.current = { days: [], unscheduled: [], transports: [], destinations: [] };
  }, [tripId]);

  /** Show this itinerary now (the refs too, so an edit in the same tick builds on it). */
  const installItinerary = useCallback((it) => {
    tripDaysRef.current = it.days;
    setTripDays(it.days);
    unscheduledRef.current = it.unscheduled || [];
    setUnscheduled(it.unscheduled || []);
    transportsRef.current = it.transports || [];
    setTransports(it.transports || []);
    if (Array.isArray(it.destinations)) {
      destinationsRef.current = it.destinations;
      setDestinationsState(it.destinations);
    }
  }, []);
  const currentItinerary = () => ({
    days: tripDaysRef.current, unscheduled: unscheduledRef.current, transports: transportsRef.current, destinations: destinationsRef.current
  });

  const {
    enqueue: enqueueOps, replayable: replayableOps, noteServerRead, kick: kickOps, pendingCount: pendingOps,
    notices: coeditNotices, dismiss: dismissCoeditNotice, keepTheirs: keepTheirChange, restoreWithChanges, undo: undoChange,
    discard: discardPendingOps
  } = useTripOpsSync({
    tripId,
    userId: user?.id || null,
    enabled: opsMode,
    // Cleared the moment the trip changes (the effect above), before opsMode
    // catches up: nothing of the next trip's is restored or sent on the last
    // one's ops mode.
    isActive: () => opsModeRef.current,
    getKnownRevision: () => knownRevisionRef.current,
    setKnownRevision: (revision) => { knownRevisionRef.current = revision; },
    absorb: (entries) => { confirmedRef.current = replayItinerary(confirmedRef.current, entries); },
    onChanged: () => {
      installItinerary(replayItinerary(confirmedRef.current, replayableOps()));
    },
    requestReload: () => reloadTripRef.current?.(),
    // Something landed: the trip lists (Planner, Home "Coming up") and the
    // trip's cached copy are out of date, as after a save.
    onApplied: (forTrip) => {
      invalidateTripCache(forTrip);
      invalidatePlannerCache(user?.id);
      invalidateDashboardCache(user?.id);
    },
    canSend: (op, comingDays) => {
      // No server copy of this trip read yet: nothing to judge a day by.
      if (knownRevisionRef.current === null && !(confirmedRef.current.days || []).length) return 'wait';
      // A day the planner shows, the server has, or a day.add on its way
      // creates; an op naming any other (removed before the op went) can
      // never land.
      const known = (uid) => (comingDays && comingDays.has(uid))
        || (tripDaysRef.current || []).some(d => d.uid === uid)
        || (confirmedRef.current.days || []).some(d => d.uid === uid);
      return dayUidsNamed(op).every(known) ? 'send' : 'drop';
    },
    // A day or trip notice's subject.
    labelFor: (op) => {
      if (op.type === 'trip.set') return metaRef.current.trip?.name || null;
      const day = (tripDaysRef.current || []).find(d => d.uid === op.target);
      return day ? formatDateWithWeekdayShort(day.date) : null;
    },
    // "Keep theirs" on a trip setting: the form and the baseline take it.
    onTripSettingKept: (field, value) => {
      const setters = { name: setTripName, location: setTripLocation, start_date: setStartDate, end_date: setEndDate, trip_type: setTripType, description: setDescription, itinerary_view_preference: setItineraryViewPreference };
      if (field === 'location') prevTripLocationRef.current = value || '';
      if (setters[field]) setters[field](value || '');
      setTrip(prev => (prev ? { ...prev, [field]: value } : prev));
    },
    stopNamed: (key) => {
      for (const day of [...(tripDaysRef.current || []), { uid: null, activities: unscheduledRef.current || [] }]) {
        const stop = (day.activities || []).find(a => opStopKey(a) === key);
        if (stop) return { stop, dayUid: day.uid };
      }
      return null;
    }
  });

  /**
   * In ops mode, every itinerary edit comes through here: `updater` takes the
   * itinerary ({ days, unscheduled, transports }) and returns the next one;
   * planItineraryOps compares the two and returns the ops that say what
   * changed (every edit path goes through the funnel, so none can forget its
   * op) and the itinerary with the order keys they chose, which is shown.
   * `opts`: what a removed day's stops do on the server (dayRemoval), and
   * the trip dates this edit set (dates/seenDates).
   */
  const mutateItinerary = useCallback((updater, opts = {}) => {
    const prev = currentItinerary();
    const raw = updater(prev);
    const datesChanged = opts.dates && Object.keys(opts.dates).some(k => (opts.seenDates || {})[k] !== opts.dates[k]);
    if (!raw || (raw.days === prev.days && raw.unscheduled === prev.unscheduled && raw.transports === prev.transports
      && raw.destinations === prev.destinations && !datesChanged)) return;
    // Every walk in step with its places (utils/dayWalks): a walk moved takes
    // its places, a place moved resizes its walk — one edit, one gesture.
    // A stop given another time is a plan: "I'm here" no longer ends it.
    const next = { ...raw, days: withHereClearedOnRetime(prev.days, walksInStep(prev.days, withOpIdentity(withClientIds(raw.days)))) };
    const { itinerary, entries } = planItineraryOps(prev, next, opts);
    installItinerary(itinerary);
    enqueueOps(entries);
  }, [installItinerary, enqueueOps]); // eslint-disable-line react-hooks/exhaustive-deps

  // Itinerary mutations go through these (now pure pass-throughs) — kept as
  // the single mutation funnel so the no-op convention stays discoverable:
  // ops signal "nothing changed" by returning their input array, which keeps
  // the identity clean and prevents byte-identical autosave PUTs.
  // Every stop added through here gets its own minted id (withClientIds),
  // so a save can name it before any answer — see plannerSaveDiff.
  // In ops mode they are planned as ops (mutateItinerary).
  const mutateTripDays = useCallback((updater) => {
    if (!opsModeRef.current) {
      setTripDays(prev => withHereClearedOnRetime(prev, walksInStep(prev, withClientIds(typeof updater === 'function' ? updater(prev) : updater))));
      return;
    }
    mutateItinerary(it => ({ ...it, days: typeof updater === 'function' ? updater(it.days) : updater }));
  }, [mutateItinerary]);
  const mutateTransports = useCallback((updater) => {
    if (!opsModeRef.current) { setTransports(updater); return; }
    mutateItinerary(it => ({ ...it, transports: typeof updater === 'function' ? updater(it.transports) : updater }));
  }, [mutateItinerary]);
  // The Trip setup place chips (D11): ops like the rest of the itinerary.
  const setDestinations = useCallback((updater) => {
    if (!opsModeRef.current) { setDestinationsState(updater); return; }
    mutateItinerary(it => ({ ...it, destinations: typeof updater === 'function' ? updater(it.destinations || []) : updater }));
  }, [mutateItinerary]);

  // Add or replace one transport segment, matched by clientUid (the stable
  // identity the editor mints on first save).
  const upsertTransport = useCallback((data) => {
    if (!data?.clientUid) return;
    mutateTransports(prev => {
      const idx = prev.findIndex(t => t.clientUid === data.clientUid);
      if (idx === -1) return [...prev, data];
      const next = [...prev];
      next[idx] = { ...next[idx], ...data };
      return next;
    });
  }, [mutateTransports]);

  const removeTransport = useCallback((clientUid) => {
    mutateTransports(prev => prev.filter(t => t.clientUid !== clientUid));
  }, [mutateTransports]);

  // The trip's own settings (name, place, dates, type, description) changed
  // here and not saved: they wait for Save (the itinerary doesn't — in ops
  // mode every edit goes as it is made). Shown as a note in Trip setup.
  // Compared trimmed: a save stores the trimmed text, and a trailing space
  // is no change. (The itinerary view isn't one: it is a way of looking,
  // saved as switched in ops mode — it counts for leaving below.)
  const unsavedSettings = useMemo(() => {
    if (!trip || !canEdit) return false;
    return tripName.trim() !== (trip.name || '') ||
      tripLocation.trim() !== (trip.location || '') ||
      startDate !== (trip.start_date || '') ||
      endDate !== (trip.end_date || '') ||
      tripType !== (trip.trip_type || 'leisure') ||
      description.trim() !== (trip.description || '') ||
      // Chips wait for Save on the whole-itinerary save (ops send them).
      (!opsMode && !sameDestinations(destinations, trip.destinations));
  }, [trip, canEdit, tripName, tripLocation, startDate, endDate, tripType, description, opsMode, destinations]);

  // Derived: unsaved changes detection (includes both metadata AND tripDays)
  const hasUnsavedChanges = useMemo(() => {
    if (!trip) return false;
    // Nothing a view-only traveller holds can be saved (the server refuses
    // it), so there is nothing to warn about losing.
    if (!canEdit) return false;

    if (unsavedSettings) return true;
    if (itineraryViewPreference !== (trip.itinerary_view_preference || 'high-level')) return true;
    // A new trip's Who's going picks live only here until the create sends
    // them: leaving (the setup sheet's X with no name) must ask first. You
    // alone (the picker returns you) is nothing to lose.
    if (isNewTrip && selectedFriends.some(f => (f.user_id || f.id) !== user?.id)) return true;

    // Ops mode: the itinerary has no save of its own — every edit goes as it
    // is made, and one not sent yet goes on leaving or waits on the device
    // (D6): nothing of it is lost by leaving, so only the settings count.
    if (opsMode) return false;

    // (A chip changed and not saved is in unsavedSettings: the whole-itinerary
    // autosave runs on itinerary edits only, so Save takes it — and the switch
    // to ops mode waits for it, as that switch re-reads.)

    // Compare itinerary identity against the last loaded/saved snapshot
    // instead of deep-diffing. The saved ref only moves inside load/save
    // bookkeeping, each of which also sets state (trip, tripDays), so the
    // memo always recomputes after it changes.
    if (savedItineraryRef.current !== null) {
      return tripDays !== savedItineraryRef.current.tripDays
        || transports !== savedItineraryRef.current.transports;
    }

    return false;
  }, [canEdit, trip, tripDays, transports, opsMode, unsavedSettings, itineraryViewPreference, isNewTrip, selectedFriends, user?.id]);

  // Derived: trip duration
  const tripDuration = useMemo(() => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate + 'T12:00:00');
    const end = new Date(endDate + 'T12:00:00');
    return Math.ceil(Math.abs(end - start) / (1000 * 60 * 60 * 24)) + 1;
  }, [startDate, endDate]);

  // Initialize new trip with URL params — once. searchParams gets a new
  // identity on every ?tab= change (setActiveTab clones it), and re-running
  // this effect reverted user-edited dates back to the URL values and reset
  // the change-detection baseline mid-edit.
  const didInitNewTripRef = useRef(false);
  useEffect(() => {
    if (isNewTrip && !didInitNewTripRef.current) {
      didInitNewTripRef.current = true;
      const urlStartDate = searchParams?.get('startDate');
      const urlEndDate = searchParams?.get('endDate');
      const urlLocation = searchParams?.get('location');

      if (urlStartDate) setStartDate(urlStartDate);
      if (urlEndDate) setEndDate(urlEndDate);
      if (urlLocation) {
        setTripLocation(urlLocation);
        prevTripLocationRef.current = urlLocation;
      }

      setTrip({
        id: null,
        name: '',
        location: '',
        start_date: urlStartDate || '',
        end_date: urlEndDate || '',
        trip_type: 'leisure',
        description: '',
        status: 'planning'
      });
      setTripOwnerId(user?.id || null);
      setCurrentUserRole('owner');
      setCanEdit(true);
      setLoading(false);
    }
  }, [isNewTrip, searchParams, user?.id]);

  // Apply a trip payload (from cache or network) to local state.
  // Deep-clones the source so in-place mutations to React state (e.g. booking-url
  // edits, drag reorders) can't corrupt the cached entry.
  const applyTripData = useCallback((source) => {
    const tripData = JSON.parse(JSON.stringify(source));
    setTrip(tripData);
    setTripName(tripData.name || '');
    const loadedLocation = tripData.location || '';
    setTripLocation(loadedLocation);
    prevTripLocationRef.current = loadedLocation;
    setStartDate(tripData.start_date || '');
    setEndDate(tripData.end_date || '');
    setTripType(tripData.trip_type || 'leisure');
    setDescription(tripData.description || '');
    const server = {
      days: tripData.tripDays || [],
      unscheduled: tripData.unscheduledActivities || [],
      transports: tripData.transports || [],
      destinations: tripData.destinations || []
    };
    confirmedRef.current = server;
    // In ops mode the ops still pending go on top (an invite refetch mid-edit),
    // less the ones this copy already holds.
    if (opsModeRef.current) noteServerRead(Number.isInteger(tripData.revision) ? tripData.revision : null);
    const loaded = opsModeRef.current ? replayItinerary(server, replayableOps()) : server;
    installItinerary(loaded);
    knownRevisionRef.current = Number.isInteger(tripData.revision) ? tripData.revision : null;
    // Loaded state IS the saved state — baseline the identity snapshot.
    savedItineraryRef.current = { tripDays: loaded.days, transports: loaded.transports };
    setSelectedFriends(tripData.travelers || []);
    setItineraryViewPreference(tripData.itinerary_view_preference || 'high-level');

    const { isMember, role, canEdit: userCanEdit } = tripEditRights(tripData, user?.id);
    const wasNonMember = loadedAsNonMemberRef.current;
    loadedAsNonMemberRef.current = !isMember;
    setCanEdit(userCanEdit);
    setTripOwnerId(tripData.user_id);
    setCurrentUserRole(role);
    rightsRef.current = { canEdit: userCanEdit, role };

    if (!userCanEdit) {
      setIsEditMode(false);
    } else if (wasNonMember && !isViewModeRef.current) {
      // Just joined (C114): open in edit mode, as a member's first load does.
      setIsEditMode(true);
    }
  }, [user?.id, installItinerary, replayableOps, noteServerRead]);

  // Fetch existing trip data with AbortController. Serves from cache if fresh.
  const fetchTrip = useCallback(async (signal) => {
    if (isNewTrip || !tripId) return;

    const cached = getTripCached(user?.id, tripId);
    if (cached) {
      applyTripData(cached);
      setLoading(false);
      setError(null);
      // Shown at once, then re-read: the copy can be up to a minute old, and
      // the itinerary and edit rights (whose live notice reaches only an open
      // planner) must not wait for the next event to catch up.
      reloadTripRef.current?.();
      return;
    }

    const answersAtStart = inviteAnswersRef.current;
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE}/api/trips/${tripId}`, {
        headers: AuthService.getAuthHeaders(),
        signal
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Trip not found');
        }
        throw new Error('Failed to fetch trip');
      }

      const data = await response.json();
      const tripData = data.trip || data;

      applyTripData(tripData);
      if (inviteAnswersRef.current !== answersAtStart && loadedAsNonMemberRef.current) {
        // An invitation was answered while this was in flight, and the copy
        // is a non-member's: it may predate joining, so neither keep nor
        // cache it — fetch again (C114).
        refetchAfterJoinRef.current?.();
      } else {
        setTripCached(user?.id, tripId, tripData);
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Error fetching trip:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [tripId, isNewTrip, user?.id, applyTripData]);

  // The trip's settings as the form holds them and as they were loaded, for
  // a re-read (declared above it) to compare.
  const metaRef = useRef({});
  metaRef.current = { tripName, tripLocation, startDate, endDate, tripType, description, itineraryViewPreference, trip };

  /**
   * Ops mode: a co-editor's change to the trip's settings — its dates above
   * all, an op of their own since Stage D — taken on a re-read wherever this
   * planner isn't editing that setting and has no change of its own on the
   * way (a pending trip.set: the read can predate it).
   */
  const syncTripSettings = useCallback((tripData, pendingEntries) => {
    const m = metaRef.current;
    const base = m.trip || {};
    const pendingFields = new Set();
    (pendingEntries || []).forEach(e => {
      if (e.op && e.op.type === 'trip.set') Object.keys(e.op.fields || {}).forEach(k => pendingFields.add(k));
    });
    const settings = [
      ['name', m.tripName, setTripName, ''], ['location', m.tripLocation, setTripLocation, ''],
      ['start_date', m.startDate, setStartDate, ''], ['end_date', m.endDate, setEndDate, ''],
      ['trip_type', m.tripType, setTripType, 'leisure'], ['description', m.description, setDescription, ''],
      ['itinerary_view_preference', m.itineraryViewPreference, setItineraryViewPreference, 'high-level']
    ];
    const taken = {};
    settings.forEach(([key, form, set, fallback]) => {
      if (pendingFields.has(key)) return;
      const loaded = base[key] || fallback;
      if ((form ?? fallback) !== loaded) return; // being edited here
      const server = tripData[key] || fallback;
      if (server === loaded) return;
      // Location first into the sync guard, so taking it never repaints days.
      if (key === 'location') prevTripLocationRef.current = server;
      set(server);
      taken[key] = tripData[key] ?? null;
    });
    if (Object.keys(taken).length) setTrip(prev => (prev ? { ...prev, ...taken } : prev));
  }, []);

  /**
   * A re-read's check of this person's edit rights (both re-read paths).
   * Returns true when it took the server's trip whole (rights lost, or just
   * joined), so the caller stops there.
   */
  const rightsChangedOnReread = useCallback((tripData, requestedTripId) => {
    // Someone changed this person's edit rights (PATCH .../permissions →
    // trip_changed with access_changed, or any re-read that finds them
    // moved). Lost: take the server's trip whole, as a fresh load would —
    // unsaved edits here would only be refused — and drop to view mode.
    // Joined (accepted on another device): a non-member's copy can lack
    // activities (C114) and they had nothing to edit, so take it whole too
    // — that is also what clears loadedAsNonMemberRef.
    const rights = tripEditRights(tripData, user?.id);
    const before = rightsRef.current;
    const lost = !!before && before.canEdit && !rights.canEdit;
    const joined = !!before && loadedAsNonMemberRef.current && rights.isMember;
    if (lost || joined) {
      applyTripData(tripData);
      mergedIdsRef.current = new Set();
      sentIdsRef.current = new Set();
      setTripCached(user?.id, requestedTripId, tripData);
      // Only someone still on the trip is told what is theirs to add: a
      // traveller removed from a public trip re-reads as a visitor.
      let kind = null;
      if (rights.isMember && lost) kind = 'lost';
      else if (joined && rights.canEdit) kind = CO_ORGANIZER_ROLES.includes(rights.role) ? 'coOrganizer' : 'gained';
      setAccessNotice(kind ? { kind } : null);
      return true;
    }
    if (before && (before.canEdit !== rights.canEdit || before.role !== rights.role)) {
      const wasCoOrganizer = CO_ORGANIZER_ROLES.includes(before.role);
      const isCoOrganizer = CO_ORGANIZER_ROLES.includes(rights.role);
      rightsRef.current = { canEdit: rights.canEdit, role: rights.role };
      setCanEdit(rights.canEdit);
      setCurrentUserRole(rights.role);
      setSelectedFriends(tripData.travelers || []);
      if (!before.canEdit && rights.canEdit && !isViewModeRef.current) setIsEditMode(true);
      setAccessNotice({
        kind: isCoOrganizer && !wasCoOrganizer ? 'coOrganizer'
          : !before.canEdit ? 'gained'
            : wasCoOrganizer && !isCoOrganizer ? 'notCoOrganizer'
              : null
      });
    }
    return false;
  }, [user?.id, applyTripData]);

  // Re-read the ITINERARY from the server WITHOUT the loading state. For
  // writes made elsewhere on the page — the recap composer adds itinerary
  // stops directly. Without this the planner keeps its stale tripDays, and
  // its next autosave (a full replace, clearExisting) soft-deletes the stops
  // it never saw. Silent because setLoading would unmount the open composer.
  //
  // Itinerary only: trip settings (name, dates, description…) are never
  // autosaved, so an edit to them waiting on Save must survive the reload.
  // And when the itinerary itself has edits the server hasn't taken (a
  // failed autosave, or the 2s debounce), only the stops the caller names
  // (`addedActivityIds`, which the recap composer just created) are merged
  // into them — the baseline stays put, so the next autosave sends both.
  // Never "every server stop the page lacks": a stop the user deleted but
  // hasn't saved looks exactly like that, and so does a stop the planner
  // added and saved this session (it keeps its client id locally, so its
  // server copy's id matches nothing) — merging those resurrected deletes
  // and saved duplicates.
  // `refreshedPlaceIds`: places whose name/position the server just looked up
  // (refreshPlaceBasics, below). With edits pending, those stops take the
  // server's display fields so they render — and autosave — as place stops.
  const reloadTrip = useCallback(async ({ addedActivityIds = [], refreshedPlaceIds = [] } = {}) => {
    if (isNewTrip || !tripId) return;
    if (opsModeRef.current) {
      // Ops mode: the server's copy with the pending ops replayed — the whole
      // merge (plan §4.8).
      const requestedTripId = tripId;
      const requestSeq = ++reloadSeqRef.current;
      try {
        const response = await fetch(`${API_BASE}/api/trips/${requestedTripId}`, { headers: AuthService.getAuthHeaders() });
        if (!response.ok) return;
        const data = await response.json();
        if (currentTripIdRef.current !== requestedTripId || !opsModeRef.current) return;
        if (requestSeq <= reloadAppliedSeqRef.current) return;
        // Read before something this planner already knows landed (an op's
        // answer, a day save): it would undo it here.
        const readRevision = Number.isInteger((data.trip || data).revision) ? (data.trip || data).revision : null;
        if (readRevision !== null && Number.isInteger(knownRevisionRef.current) && readRevision < knownRevisionRef.current) return;
        reloadAppliedSeqRef.current = requestSeq;
        const tripData = JSON.parse(JSON.stringify(data.trip || data));
        if (rightsChangedOnReread(tripData, requestedTripId)) return;
        const server = {
          days: tripData.tripDays || [],
          unscheduled: tripData.unscheduledActivities || [],
          transports: tripData.transports || [],
          destinations: tripData.destinations || []
        };
        const revision = Number.isInteger(tripData.revision) ? tripData.revision : null;
        noteServerRead(revision);
        confirmedRef.current = server;
        const shown = replayItinerary(server, replayableOps());
        installItinerary(shown);
        savedItineraryRef.current = { tripDays: shown.days, transports: shown.transports };
        knownRevisionRef.current = revision;
        setTrip(prev => (prev ? { ...prev, tripDays: server.days, transports: server.transports, destinations: server.destinations } : prev));
        syncTripSettings(tripData, replayableOps());
        setTripCached(user?.id, requestedTripId, tripData);
        kickOps();
      } catch (err) {
        console.error('Error reloading trip:', err);
      }
      return;
    }
    const requestedTripId = tripId;
    const requestSeq = ++reloadSeqRef.current;
    reloadPendingRef.current.set(requestSeq, { addedActivityIds, refreshedPlaceIds });
    // The baseline as this request leaves. A save that lands while the GET is
    // in flight moves it — and the GET may have read the rows before that
    // save committed, so its copy can't be trusted as "the saved itinerary".
    const savedAtStart = savedItineraryRef.current;
    // An invitation answered while this is in flight makes its copy older
    // than the answer's own refetch (the invites effect below), which may
    // already have applied — never lay the pre-answer copy over it (C114).
    const answersAtStart = inviteAnswersRef.current;
    try {
      const response = await fetch(`${API_BASE}/api/trips/${requestedTripId}`, {
        headers: AuthService.getAuthHeaders()
      });
      if (!response.ok) return;
      const data = await response.json();
      // The page moved to another trip while this was in flight: applying
      // this one's itinerary there would let the next autosave write it in.
      if (currentTripIdRef.current !== requestedTripId) return;
      if (inviteAnswersRef.current !== answersAtStart) return;
      // A newer reload already applied (and took this one's ids with it).
      if (requestSeq <= reloadAppliedSeqRef.current) return;
      reloadAppliedSeqRef.current = requestSeq;
      const { addedActivityIds: addedIds, refreshedPlaceIds: refreshedIds } =
        takePendingReloadIds(reloadPendingRef.current, requestSeq);
      const tripData = JSON.parse(JSON.stringify(data.trip || data));

      if (rightsChangedOnReread(tripData, requestedTripId)) return;

      // The chips: a co-editor's (an op since D11) are taken when this
      // planner hasn't changed its own, so its next save doesn't put back
      // the list it loaded — and not from a read a save of its own overtook
      // (it would hide the chip just saved). Unchanged, nothing is set: a new
      // array would re-arm the autosave's countdown on every re-read.
      const loadedDestinations = tripData.destinations || [];
      if (savedAtStart === savedItineraryRef.current
          && sameDestinations(destinationsRef.current, metaRef.current.trip?.destinations)
          && !sameDestinations(loadedDestinations, destinationsRef.current)) {
        destinationsRef.current = loadedDestinations;
        setDestinationsState(loadedDestinations);
        setTrip(prev => (prev ? { ...prev, destinations: loadedDestinations } : prev));
      }

      const loadedDays = tripData.tripDays || [];
      const loadedTransports = tripData.transports || [];
      const clean = reloadIsClean({
        savedAtStart,
        saved: savedItineraryRef.current,
        tripDays: tripDaysRef.current,
        transports: transportsRef.current
      });
      if (clean) {
        // Stops added here keep the display id an open edit sheet holds.
        const shownDays = keepLocalIds(loadedDays, tripDaysRef.current, serverIdsRef.current);
        setTripDays(shownDays);
        setTransports(loadedTransports);
        savedItineraryRef.current = { tripDays: shownDays, transports: loadedTransports };
        // The baseline is the server's own now: nothing merged or in flight
        // needs naming beyond it.
        mergedIdsRef.current = new Set();
        sentIdsRef.current = new Set();
        knownRevisionRef.current = Number.isInteger(tripData.revision) ? tripData.revision : null;
        setTrip(prev => (prev ? { ...prev, tripDays: loadedDays, transports: loadedTransports } : prev));
        setTripCached(user?.id, requestedTripId, tripData);
      } else {
        // Only stops the merge actually placed count as shown: one the GET
        // put in no day (its day was deleted) isn't on screen, and naming it
        // removed would delete a co-editor's stop no one here saw.
        setTripDays(prev => applyRefreshedPlaces(
          mergeServerStops(prev, loadedDays, addedIds, mergedIdsRef.current), loadedDays, refreshedIds));
      }
    } catch (err) {
      console.error('Error reloading trip:', err);
    } finally {
      // A request that never applied (failed, left for another trip, or
      // overtaken) leaves nothing behind for a later response to take.
      reloadPendingRef.current.delete(requestSeq);
    }
  }, [tripId, isNewTrip, user?.id, rightsChangedOnReread, installItinerary, replayableOps, noteServerRead, kickOps, syncTripSettings]);
  reloadTripRef.current = reloadTrip;

  // Live: someone else saved this trip (server/services/tripLiveUpdates.js)
  // → re-read. Clean, the re-read takes the server's itinerary whole; with
  // edits pending it keeps them, and this planner's next save is told what
  // the server kept (applySavedMetadata re-reads then). Coalesced, because a
  // co-editor's autosaves arrive every couple of seconds while they work.
  // The stream closes while the tab is hidden, so coming back into view
  // re-reads too — the phone case: switch apps, come back, see her changes.
  useEffect(() => {
    if (isNewTrip || !tripId) return undefined;
    let timer = null;
    const schedule = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { timer = null; reloadTrip(); }, 800);
    };
    const off = notificationsServiceDB.onTripChanged((data) => {
      // `reconnected`: the stream reopened, so any trip may have moved.
      if (!data || (data.trip_id !== tripId && !data.reconnected) || data.origin === EDIT_ORIGIN) return;
      if (savedItineraryRef.current === null) return; // first load still in flight
      // This person's edit rights changed: nothing in the plan moved, so the
      // revision didn't either — re-read anyway (reloadTrip applies them).
      if (data.access_changed) { schedule(); return; }
      // Already read (a duplicate announce, or a save this planner made from
      // that revision).
      if (Number.isInteger(data.revision) && knownRevisionRef.current !== null
        && data.revision <= knownRevisionRef.current) return;
      schedule();
    });
    const onVisible = () => {
      if (document.visibilityState === 'visible' && savedItineraryRef.current !== null) schedule();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      off();
      document.removeEventListener('visibilitychange', onVisible);
      if (timer) clearTimeout(timer);
    };
  }, [tripId, isNewTrip, reloadTrip]);

  // Fetch trip on mount with cleanup
  useEffect(() => {
    if (!isNewTrip && tripId) {
      const controller = new AbortController();
      fetchTrip(controller.signal);
      return () => controller.abort();
    }
  }, [tripId, isNewTrip, fetchTrip]);

  // A Google-place stop with no name and no position of ours — its 30-day
  // Google row has gone and no landmark is linked to it (a linked one already
  // reads the landmark's name and position from the server) — is named again
  // with the cheapest Google lookup that names a place (name + position; see
  // server/services/placeBasicsRefresh.js), then the trip is re-read. Members
  // only, each place once a session: a place Google can't name isn't retried
  // on every load (Kurtis, 2026-10-05). A request that gets no answer (rate
  // limit, cost cap, network) un-marks its places, so the next load asks again.
  useEffect(() => {
    if (!trip || isNewTrip || !tripId || !user?.id || loadedAsNonMemberRef.current) return;
    // The live stops, not trip.tripDays (the copy as loaded): a stop the user
    // has since deleted must not be looked up when a failed request retries.
    const placeIds = unnamedGooglePlaceIds(tripDaysRef.current).filter(id => !refreshAttemptedPlaceIds.has(id));
    if (placeIds.length === 0) return;
    placeIds.forEach(id => refreshAttemptedPlaceIds.add(id));
    (async () => {
      const named = [];
      // One batch at a time, all in this pass: a later batch must not wait
      // for `trip` to change again, which a dirty reload never does.
      for (let i = 0; i < placeIds.length; i += PLACE_REFRESH_BATCH) {
        const batch = placeIds.slice(i, i + PLACE_REFRESH_BATCH);
        let data = null;
        try {
          const response = await fetch(`${API_BASE}/api/places/refresh-basic`, {
            method: 'POST',
            headers: { ...AuthService.getAuthHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ place_ids: batch })
          });
          if (response.ok) data = await response.json();
        } catch (err) {
          console.warn('Place name refresh failed:', err.message);
        }
        if (!data) {
          placeIds.slice(i).forEach(id => refreshAttemptedPlaceIds.delete(id));
          break;
        }
        // Skipped ones have data on the server too (someone else's lookup, a
        // landmark linked since): this copy of the trip is stale for them.
        named.push(...(data.refreshed || []), ...Object.keys(data.skipped || {}));
      }
      if (named.length > 0) reloadTrip({ refreshedPlaceIds: named });
    })();
  }, [trip, isNewTrip, tripId, user?.id, reloadTrip]);

  // An invitation was answered (the notification center is an overlay, so
  // this page can be underneath it). A viewer who loaded the trip as a
  // non-member gets a fresh copy, with their new permissions and every
  // activity a member sees (C114) — silently, like reloadTrip, so the page
  // never drops to its skeleton for an answer about another trip. Members are
  // left alone: refetching would drop their unsaved edits. A non-member has
  // none (they cannot edit). 'invitesChanged' is cacheInvalidation's
  // INVITES_CHANGED_EVENT, spelled out because that module imports this one.
  useEffect(() => {
    if (isNewTrip || !tripId) return undefined;
    let controller = null;
    const refetch = async () => {
      if (controller) controller.abort();
      controller = new AbortController();
      const answersAtStart = inviteAnswersRef.current;
      try {
        const response = await fetch(`${API_BASE}/api/trips/${tripId}`, {
          headers: AuthService.getAuthHeaders(),
          signal: controller.signal
        });
        if (!response.ok) return;
        const data = await response.json();
        if (currentTripIdRef.current !== tripId) return;
        const tripData = data.trip || data;
        applyTripData(tripData);
        if (inviteAnswersRef.current === answersAtStart || !loadedAsNonMemberRef.current) {
          setTripCached(user?.id, tripId, tripData);
        }
      } catch (err) {
        if (err.name !== 'AbortError') console.error('Error refetching trip after an invite:', err);
      }
    };
    refetchAfterJoinRef.current = refetch;
    const onInvitesChanged = () => {
      inviteAnswersRef.current += 1;
      if (!loadedAsNonMemberRef.current) return;
      invalidateTripCache(tripId);
      refetch();
    };
    window.addEventListener('invitesChanged', onInvitesChanged);
    return () => {
      window.removeEventListener('invitesChanged', onInvitesChanged);
      if (refetchAfterJoinRef.current === refetch) refetchAfterJoinRef.current = null;
      if (controller) controller.abort();
    };
  }, [tripId, isNewTrip, user?.id, applyTripData]);

  // Auto-generate trip days for new trips when dates are set
  useEffect(() => {
    if (loading) return;
    // Not in ops mode: a re-read that briefly holds no days (two people each
    // removing a day of a two-day trip) must not have every open planner
    // add them all back.
    const datesPickedHere = startDate !== (trip?.start_date || '') || endDate !== (trip?.end_date || '');
    if (startDate && endDate && tripDays.length === 0 && (!opsModeRef.current || datesPickedHere)) {
      mutateTripDays(ops.generateTripDays(startDate, endDate, tripLocation));
    }
  }, [mutateTripDays, loading, startDate, endDate, tripDays.length, tripLocation, trip?.start_date, trip?.end_date]);

  // Sync day locations when the trip-level location changes
  // Updates days that have the old location OR have no location set (empty
  // string). Multi-location days (travel days like "Antwerp → London") are
  // deliberately skipped — their stop list was hand-built and shouldn't be
  // flattened by a trip-level rename.
  useEffect(() => {
    const prevLocation = prevTripLocationRef.current;
    prevTripLocationRef.current = tripLocation;

    if (!tripLocation || prevLocation === tripLocation) return;
    if (tripDays.length === 0) return;

    if (opsModeRef.current) {
      // Ops mode: the trip's location goes with the days it repaints (the
      // whole-itinerary save used to carry both), and is saved by it.
      const was = metaRef.current.trip?.location || null;
      mutateItinerary(it => ({ ...it, days: ops.syncDayLocations(it.days, prevLocation, tripLocation, isMultiLocationDay) }),
        { dates: { location: tripLocation }, seenDates: { location: was } });
      setTrip(prev => (prev ? { ...prev, location: tripLocation } : prev));
      return;
    }
    mutateTripDays(prev => ops.syncDayLocations(prev, prevLocation, tripLocation, isMultiLocationDay));
    // tripDays is a dep only to satisfy exhaustive-deps: runs triggered by
    // itinerary edits hit the prevLocation === tripLocation early-return.
  }, [mutateTripDays, mutateItinerary, tripLocation, tripDays]);

  // Single source for the PUT/POST body — autosave and manual save share it
  // so the two can't drift (a field present in one payload but missing from
  // the other's post-save merge left hasUnsavedChanges permanently stuck on).
  // The removals are read from refs at build time — autosave builds when it
  // fires, against the baseline as it stands then.
  const buildTripPayload = useCallback((overrides = {}) => ({
    name: tripName.trim(),
    location: tripLocation.trim(),
    startDate: startDate || null,
    endDate: endDate || null,
    tripType,
    description: description.trim(),
    tripDays: stripPhotosForSave(withServerIds(tripDays, serverIdsRef.current)),
    ...(isNewTrip ? {} : namedRemovals({
      baselineDays: savedItineraryRef.current?.tripDays || [],
      currentDays: tripDays,
      baselineTransports: savedItineraryRef.current?.transports || [],
      currentTransports: transports,
      idMap: serverIdsRef.current,
      extraActivityIds: [...mergedIdsRef.current, ...noteSentIds(tripDays, sentIdsRef.current)]
    })),
    // The chips only when this planner changed them: the server replaces the
    // whole list, so echoing the loaded copy would delete a chip a co-editor
    // added since (by op, D11) on every autosave.
    ...(isNewTrip || !sameDestinations(destinations, trip?.destinations) ? { destinations } : {}),
    edit_origin: EDIT_ORIGIN,
    // Travellers ride along on CREATE only (BH-045). An existing trip gains
    // companions through POST /api/trips/:id/travelers (TripSetupDrawer's
    // handleSaveGroup); echoing the roster this tab loaded re-invited anyone
    // removed since, and the server now ignores the field on a PUT anyway.
    ...(isNewTrip ? { travelers: selectedFriends } : {}),
    transports,
    itineraryViewPreference,
    status: 'planning',
    ...overrides
  }), [tripName, tripLocation, startDate, endDate, tripType, description, tripDays, destinations, trip, isNewTrip, selectedFriends, transports, itineraryViewPreference]);

  // Post-save bookkeeping shared by autosave and manual save: bust the list
  // caches (trip metadata feeds Planner's trip list + Home "Coming Up") and
  // re-baseline `trip` so metadata change detection resets. The merge must
  // mirror buildTripPayload field-for-field.
  const applySavedMetadata = useCallback((savedTripId, effectiveEndDate, result = null) => {
    invalidateTripCache(savedTripId);
    invalidatePlannerCache(user?.id);
    invalidateDashboardCache(user?.id);
    // The page stays mounted across trips (/trip/:id is not keyed): a late
    // answer for the trip just left must not touch the one now open — its
    // ids, its revision, its name and dates. A new trip's first save is
    // answered before the page moves to its id.
    if (!isNewTrip && savedTripId !== currentTripIdRef.current) return;

    // The save's answer (named removals): learn the new stops' ids; when the
    // server kept rows this planner doesn't show, or dropped ones it still
    // shows (another member deleted them), re-read. Clean, the re-read takes
    // the server's itinerary whole; with edits pending it merges the kept
    // stops in, and the gone ones are dropped here.
    if (result && !isNewTrip && !opsModeRef.current) {
      learnInsertedIds(serverIdsRef.current, result);
      // Nothing else had changed since this planner's read: the save's own
      // revision is now what it shows.
      if (Number.isInteger(result.revision) && result.revision_before === knownRevisionRef.current) {
        knownRevisionRef.current = result.revision;
      }
      const cue = saveNeedsReload(result, reloadCueSeenRef.current);
      if (cue.goneActivityIds.length > 0) {
        mutateTripDays(prev => dropActivities(prev, cue.goneActivityIds, serverIdsRef.current));
      }
      if (cue.reload) reloadTrip({ addedActivityIds: cue.keptActivityIds });
    }

    // Mirror of the server's stale-label heal (tripsService.updateTrip): when
    // this save merely ECHOED the stored location and day 1 starts somewhere
    // else, the server healed the stored value to day 1's first stop. The
    // client must adopt the same value, because the PUT response is lean and
    // this baseline is all it has — left stale, the NEXT autosave would echo
    // the old label again, which the server (correctly) reads as a deliberate
    // edit and writes back, flipping the stored label on every other save.
    //
    // prevTripLocationRef is set FIRST so the tripLocation→days sync effect
    // early-returns: this is bookkeeping about where day 1 already is, and
    // must never repaint other days the way a real location edit does.
    const echoed = tripLocation.trim();
    const storedBefore = String(trip?.location || '').trim();
    let adopted = null;
    if (trip && echoed === storedBefore) {
      const firstDate = [...new Set(tripDays.map(d => d.date))].sort()[0];
      const firstDay = tripDays.find(d => d.date === firstDate);
      const firstStop = firstDay ? String(getDayLocations(firstDay)[0]?.name || '').trim() : '';
      if (firstStop && firstStop.toLowerCase() !== echoed.toLowerCase()) {
        adopted = firstStop;
        prevTripLocationRef.current = firstStop;
        setTripLocation(firstStop);
      }
    }

    setTrip(prev => prev ? ({
      ...prev,
      name: tripName.trim(),
      location: adopted ?? tripLocation.trim(),
      start_date: startDate || null,
      end_date: (effectiveEndDate ?? endDate) || null,
      trip_type: tripType,
      description: description.trim(),
      itinerary_view_preference: itineraryViewPreference,
      destinations
    }) : prev);
  }, [trip, tripDays, tripName, tripLocation, startDate, endDate, tripType, description, itineraryViewPreference, destinations, user?.id, isNewTrip, mutateTripDays, reloadTrip]);

  // An autosave the server refused ({ status, code, error }), for the page to
  // tell the user — e.g. code 'transport_limit' (BH-031). Cleared by the page.
  const [autosaveFailure, setAutosaveFailure] = useState(null);
  const clearAutosaveFailure = useCallback(() => setAutosaveFailure(null), []);

  // Debounced autosave + unmount/beforeunload flush (see useTripAutosave).
  // buildTripPayload's identity tracks every payload input, so passing it is
  // what re-arms the debounce on each edit.
  useTripAutosave({
    tripId,
    // Ops mode sends no whole-itinerary save: every edit is an op.
    enabled: !isNewTrip && !!tripId && !loading && !opsMode,
    canSave: canEdit,
    isSaving,
    hasName: !!tripName.trim(),
    tripDays,
    transports,
    savedItineraryRef,
    buildPayload: buildTripPayload,
    onSaved: applySavedMetadata,
    onFailed: setAutosaveFailure
  });

  // Switch this planner to ops the first time it may (the rollout switch says
  // so — an admin's flag can arrive after the trip loads) and nothing is
  // unsaved, then re-read: the server's copy is the confirmed base.
  useEffect(() => {
    if (!opsEnabled || opsMode || isNewTrip || !tripId || loading) return;
    if (savedItineraryRef.current === null || hasUnsavedChanges || isSaving) return;
    const it = { ...currentItinerary(), days: withOpIdentity(tripDaysRef.current) };
    confirmedRef.current = it;
    if (it.days !== tripDaysRef.current) installItinerary(it);
    savedItineraryRef.current = { tripDays: it.days, transports: it.transports };
    opsModeRef.current = true;
    setOpsMode(true);
    reloadTripRef.current?.();
    // `trip` re-runs it once the load lands (the baseline is a ref).
  }, [opsEnabled, opsMode, isNewTrip, tripId, loading, hasUnsavedChanges, isSaving, installItinerary, trip]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ops mode: the high-level / timeline toggle is saved as it's switched
  // (it rode the itinerary's autosave before), not held for Save.
  useEffect(() => {
    // An editor's: a view-only traveller's toggle stays on their screen.
    if (!opsMode || !trip || !canEdit) return;
    const saved = trip.itinerary_view_preference || 'high-level';
    if (itineraryViewPreference === saved) return;
    enqueueOps([{
      op: { op_id: mintOpId(), type: 'trip.set', target: 'trip', fields: { itinerary_view_preference: itineraryViewPreference }, seen: { itinerary_view_preference: saved } },
      replay: { kind: 'trip', fields: { itinerary_view_preference: itineraryViewPreference } }
    }]);
    setTrip(prev => (prev ? { ...prev, itinerary_view_preference: itineraryViewPreference } : prev));
  }, [opsMode, trip, itineraryViewPreference, enqueueOps, canEdit]);

  // Save trip
  const handleSave = useCallback(async () => {
    if (!tripName.trim()) return false;

    // Safeguard: if start date exists but end date is missing or before start, default to start
    let effectiveEndDate = endDate;
    if (startDate && (!endDate || endDate < startDate)) {
      setEndDate(startDate);
      effectiveEndDate = startDate;
    }

    // Ops mode: the trip's settings go as one op (the itinerary already went,
    // edit by edit), naming only what changed against what was loaded.
    if (opsModeRef.current && !isNewTrip) {
      const now = {
        name: tripName.trim(), location: tripLocation.trim(), description: description.trim(),
        trip_type: tripType, itinerary_view_preference: itineraryViewPreference,
        start_date: startDate || null, end_date: effectiveEndDate || null
      };
      const was = {
        name: trip?.name || '', location: trip?.location || '', description: trip?.description || '',
        trip_type: trip?.trip_type || 'leisure', itinerary_view_preference: trip?.itinerary_view_preference || 'high-level',
        start_date: trip?.start_date || null, end_date: trip?.end_date || null
      };
      const fields = {};
      const seen = {};
      Object.keys(now).forEach(k => { if (now[k] !== was[k]) { fields[k] = now[k]; seen[k] = was[k]; } });
      if (Object.keys(fields).length) {
        enqueueOps([{ op: { op_id: mintOpId(), type: 'trip.set', target: 'trip', fields, seen }, replay: { kind: 'trip', fields } }]);
      }
      setTrip(prev => (prev ? { ...prev, ...now } : prev));
      invalidateTripCache(tripId);
      invalidatePlannerCache(user?.id);
      invalidateDashboardCache(user?.id);
      return true;
    }

    setIsSaving(true);
    // Itinerary content this save persists (the same arrays buildTripPayload
    // serializes — both close over this render's state). Edits made while the
    // request is in flight swap in new arrays, so they still read as unsaved.
    const payloadItinerary = { tripDays, transports };
    try {
      const tripData = buildTripPayload({ endDate: effectiveEndDate || null });

      const url = isNewTrip ? `${API_BASE}/api/trips` : `${API_BASE}/api/trips/${tripId}`;
      const method = isNewTrip ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify(tripData)
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to save trip');

      // Baseline first: the answer may re-read, which checks it.
      savedItineraryRef.current = payloadItinerary;
      applySavedMetadata(isNewTrip ? result.trip?.id : tripId, effectiveEndDate, result);

      return isNewTrip ? result.trip?.id : true;
    } catch (err) {
      console.error('Error saving trip:', err);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [tripName, tripLocation, description, tripType, itineraryViewPreference, trip, user?.id, startDate, endDate, isNewTrip, tripId, tripDays, transports, buildTripPayload, applySavedMetadata, enqueueOps]);

  // Trip day / activity mutations — thin wrappers over the pure ops module
  // (src/utils/tripDaysOps.js, unit-testable). Ops signal a no-op by
  // returning their input array, which mutateTripDays maps to "don't bump
  // the dirty version".
  const updateTripDay = useCallback((dayIndex, updates) => {
    mutateTripDays(prev => ops.updateDay(prev, dayIndex, updates));
  }, [mutateTripDays]);

  const addVariantForDate = useCallback((date, variantData) => {
    mutateTripDays(prev => ops.addVariantForDate(prev, date, variantData, tripLocation));
  }, [mutateTripDays, tripLocation]);


  // A saved walk (the Add sheet, a drag onto a day — utils/mapTours
  // walkStopFields) comes with its stops and ends inside it, as one change
  // (utils/walkAdd): the walk is read for them first, for up to
  // WALK_READ_MS. Unread, or not live (the save refuses the walk, and its
  // places would land loose), it goes alone as before. The day is found again
  // by its date after the read (a co-editor's change may shift indexes), on
  // this trip only (the page stays mounted across trips); a second add of the
  // same walk to the same day while the first is reading is dropped (nothing
  // shows until the read ends, so a second tap is likely).
  const walkReadsRef = useRef(new Set());
  const addCuratedWalk = useCallback((dayIndex, activity, time) => {
    const { bringWalkPlaces: _bring, ...walk } = activity;
    const target = tripDaysRef.current?.[dayIndex];
    if (!target) return;
    const requestedTripId = currentTripIdRef.current;
    const readKey = `${walk.walkId}|${target.date}|${target.plan_variant || 'A'}`;
    if (walkReadsRef.current.has(readKey)) return;
    walkReadsRef.current.add(readKey);
    const sameDay = d => d && d.date === target.date && (d.plan_variant || 'A') === (target.plan_variant || 'A');
    let timer;
    const timeout = new Promise((resolve) => { timer = setTimeout(() => resolve(null), WALK_READ_MS); });
    Promise.race([DiscoveryService.getWalk(walk.walkId).catch(() => null), timeout])
      .then((found) => {
        if (currentTripIdRef.current !== requestedTripId) return;
        const idx = (tripDaysRef.current || []).findIndex(sameDay);
        if (idx < 0) return;
        const items = found && (found.status || 'live') === 'live' ? defaultWalkPlaces(walkPlaceItems(found)) : [];
        const stops = walkWithPlaces(
          { ...walk, walkPath: walk.walkPath?.length ? walk.walkPath : (found?.path || []) },
          items,
          { day: tripDaysRef.current[idx], transports: transportsRef.current, scheduleTime: time, timeBlock: walk.timeBlock, mintId: mintActivityId }
        );
        mutateTripDays(prev => ops.addActivities(prev, idx, stops));
      })
      .finally(() => { clearTimeout(timer); walkReadsRef.current.delete(readKey); });
  }, [mutateTripDays]);

  const addActivityToDay = useCallback((dayIndex, activity, time) => {
    if (activity?.bringWalkPlaces && activity.walkId) { addCuratedWalk(dayIndex, activity, time); return; }
    mutateTripDays(prev => ops.addActivity(prev, dayIndex, activity, time));
  }, [mutateTripDays, addCuratedWalk]);

  // The map's "Select places" bulk add: several stops as ONE change (one
  // save / one co-editing op, one Undo — removeActivitiesFromDay).
  const addActivitiesToDay = useCallback((dayIndex, activities) => {
    mutateTripDays(prev => ops.addActivities(prev, dayIndex, activities));
  }, [mutateTripDays]);

  const removeActivitiesFromDay = useCallback((dayIndex, activityIds) => {
    mutateTripDays(prev => ops.removeActivities(prev, dayIndex, activityIds));
  }, [mutateTripDays]);

  const removeActivityFromDay = useCallback((dayIndex, activityIndex) => {
    mutateTripDays(prev => ops.removeActivity(prev, dayIndex, activityIndex));
  }, [mutateTripDays]);

  const updateActivityInDay = useCallback((dayIndex, activityId, updates) => {
    mutateTripDays(prev => ops.updateActivity(prev, dayIndex, activityId, updates));
  }, [mutateTripDays]);

  // Several stop edits as ONE change (one save / one co-editing gesture, one
  // Undo): `edit(days, ops)` returns the new days. The Map tab's chained
  // "I'm here" — the new stop and the one it ends — and its Undo.
  const editTripDays = useCallback((edit) => {
    mutateTripDays(prev => edit(prev, ops));
  }, [mutateTripDays]);

  // `timeBlock`: the itinerary's Reorder mode places a stop by part of day
  // (tripDaysOps.reorderActivity) — one change with the move.
  const reorderActivityInDay = useCallback((dayIndex, fromIndex, toIndex, timeBlock = null) => {
    mutateTripDays(prev => ops.reorderActivity(prev, dayIndex, fromIndex, toIndex, timeBlock));
  }, [mutateTripDays]);

  const moveActivityBetweenDays = useCallback((fromDayIndex, activityIndex, toDayIndex, timeBlock) => {
    mutateTripDays(prev => ops.moveActivityBetweenDays(prev, fromDayIndex, activityIndex, toDayIndex, timeBlock));
  }, [mutateTripDays]);

  // Day-level ops that also move the trip's date range. These read tripDays
  // from the closure (single user gestures; matches the pre-extraction
  // behavior) so they can set start/end dates from the op's result. In ops
  // mode they read the days shown NOW instead: their result is diffed into
  // ops against the live days, and a closure from before a co-editor's
  // change landed would turn her new stop into a removal (the day editor
  // calls these after a 700 ms animation).
  const daysNow = useCallback(() => (opsModeRef.current ? tripDaysRef.current : tripDays), [tripDays]);
  // A day gesture's result. In ops mode its days and the trip dates it set
  // go as one edit — the dates first, so a stop's span clamps to the new last
  // day — and the dates are saved by it, so the trip's baseline takes them.
  // `opts.dayRemoval` / `opts.toUnscheduled`: a removed day's stops (D5).
  // The trip's dates as last set: a gesture held in an older closure (Dates &
  // places' Undo, made when its toast appeared) must compare against the
  // dates now, or an Undo back to an earlier end date looks like no change.
  const tripDatesRef = useRef({ start_date: null, end_date: null });
  tripDatesRef.current = { start_date: trip?.start_date || null, end_date: trip?.end_date || null };
  const applyDayGesture = useCallback((nextDays, dates, opts = {}) => {
    if (!opsModeRef.current) { mutateTripDays(nextDays); return; }
    const sent = {};
    if (dates.start_date !== undefined && dates.start_date) sent.start_date = dates.start_date;
    if (dates.end_date !== undefined && dates.end_date) sent.end_date = dates.end_date;
    mutateItinerary(it => ({
      ...it,
      days: nextDays,
      ...(opts.toUnscheduled && opts.toUnscheduled.length ? { unscheduled: [...(it.unscheduled || []), ...opts.toUnscheduled] } : {})
    }), {
      dates: sent,
      seenDates: { ...tripDatesRef.current },
      dayRemoval: opts.dayRemoval || 'unschedule'
    });
    if (Object.keys(sent).length) {
      tripDatesRef.current = { ...tripDatesRef.current, ...sent };
      setTrip(prev => (prev ? { ...prev, ...sent } : prev));
    }
  }, [mutateTripDays, mutateItinerary]);

  const shiftAllDates = useCallback((daysToShift) => {
    const result = ops.shiftAllDates(daysNow(), daysToShift, startDate, endDate);
    applyDayGesture(result.days, { start_date: startDate ? result.startDate : undefined, end_date: endDate ? result.endDate : undefined });
    if (startDate) setStartDate(result.startDate);
    if (endDate) setEndDate(result.endDate);
  }, [applyDayGesture, daysNow, startDate, endDate]);

  const addDayAtStart = useCallback(() => {
    const result = ops.addDayAtStart(daysNow(), { startDate, tripLocation });
    applyDayGesture(result.days, { start_date: startDate ? result.startDate : undefined });
    if (startDate && result.startDate !== startDate) setStartDate(result.startDate);
  }, [applyDayGesture, daysNow, startDate, tripLocation]);

  const addDayAtEnd = useCallback(() => {
    const result = ops.addDayAtEnd(daysNow(), { endDate, tripLocation });
    applyDayGesture(result.days, { end_date: endDate ? result.endDate : undefined });
    if (endDate && result.endDate !== endDate) setEndDate(result.endDate);
  }, [applyDayGesture, daysNow, endDate, tripLocation]);

  const reorderDays = useCallback((fromDate, toDate, position) => {
    const result = ops.reorderDays(daysNow(), fromDate, toDate, position);
    if (!result) return;
    applyDayGesture(result.days, { start_date: result.startDate, end_date: result.endDate });
    setStartDate(result.startDate);
    setEndDate(result.endDate);
  }, [applyDayGesture, daysNow]);

  const insertDayAfter = useCallback((afterDate) => {
    const result = ops.insertDayAfter(daysNow(), afterDate, { tripLocation, endDate });
    if (!result) return;
    applyDayGesture(result.days, { end_date: result.endDate });
    if (result.endDate) setEndDate(result.endDate);
  }, [applyDayGesture, daysNow, tripLocation, endDate]);

  // Dates & places hands over a whole result (utils/tripDayList) — days and
  // dates together, as one gesture (in ops mode one planned edit, like the
  // day editor's). `dayRemoval`: what a removed day's plans do on the server —
  // Dates & places asks first and its "Remove day and N plans" deletes them.
  const setDaysAndRange = useCallback((result, { dayRemoval = 'remove' } = {}) => {
    if (!result) return;
    applyDayGesture(result.days, {
      start_date: result.startDate || undefined,
      end_date: result.endDate || undefined
    }, { dayRemoval });
    if (result.startDate) setStartDate(result.startDate);
    if (result.endDate) setEndDate(result.endDate);
  }, [applyDayGesture]);

  // D5: a removed day's stops go to Unscheduled (the default) or are removed
  // with it — the day editor asks (ManageDaysModal). Without ops the save
  // removes them, as before.
  const deleteDayByDate = useCallback((dateToDelete, { stops = 'unschedule', uids = null } = {}) => {
    const before = daysNow();
    // The days the person picked, by id: a co-editor's change while the
    // confirm was open may have moved another day onto that date.
    let date = dateToDelete;
    if (Array.isArray(uids) && uids.length) {
      const now = before.find(d => uids.includes(d.uid));
      if (!now) return false;
      date = now.date;
    }
    const result = ops.deleteDayByDate(before, date);
    if (!result) return false;
    // Off their day, and off its dates (put back, a stop takes its new day's).
    const goneStops = before.filter(d => d.date === date).flatMap(d => d.activities || [])
      .map(a => ({ ...a, scheduledDate: null, endDate: null }));
    applyDayGesture(result.days, { start_date: result.startDate, end_date: result.endDate }, {
      dayRemoval: stops,
      toUnscheduled: stops === 'unschedule' ? goneStops : []
    });
    setStartDate(result.startDate);
    setEndDate(result.endDate);
    return true;
  }, [applyDayGesture, daysNow]);

  // Unscheduled stops (ops mode): put one on a day (at the end of its block),
  // or remove it.
  const scheduleUnscheduled = useCallback((key, dayIndex) => {
    mutateItinerary(it => {
      const stop = (it.unscheduled || []).find(a => opStopKey(a) === key);
      if (!stop || !it.days[dayIndex]) return it;
      return {
        ...it,
        unscheduled: it.unscheduled.filter(a => a !== stop),
        days: ops.addActivity(it.days, dayIndex, stop, stop.scheduledTime || null)
      };
    });
  }, [mutateItinerary]);
  const removeUnscheduled = useCallback((key) => {
    mutateItinerary(it => ({ ...it, unscheduled: (it.unscheduled || []).filter(a => opStopKey(a) !== key) }));
  }, [mutateItinerary]);

  // Walking tour stop map refresh
  const refreshWalkingTourStopMap = useCallback((tourId, freshStops) => {
    if (!isNewTrip && tripId) {
      import('../services/walkingToursService').then(({ default: WalkingToursService }) => {
        WalkingToursService.getTripTourStopMap(tripId).then(result => {
          if (result.success) {
            setWalkingTourStopMap(result.stopMap || {});
          }
        }).catch(err => console.warn('Failed to load walking tour stop map:', err));
      });
    }

    if (tourId && freshStops) {
      mutateTripDays(prev => prev.map(day => ({
        ...day,
        activities: (day.activities || []).map(activity =>
          activity.tourId === tourId
            ? { ...activity, tourStops: freshStops }
            : activity
        )
      })));
    }
  }, [mutateTripDays, tripId, isNewTrip]);

  // Fetch the stop map only when the itinerary actually contains a walking
  // tour — the old unconditional mount fetch cost a dynamic chunk load plus a
  // request on EVERY planner open, for the majority of trips with no tours.
  const hasTourActivities = useMemo(
    () => tripDays.some(d => (d.activities || []).some(a => a.tourId)),
    [tripDays]
  );
  const tourMapFetchedRef = useRef(false);
  useEffect(() => {
    if (!hasTourActivities || tourMapFetchedRef.current) return;
    tourMapFetchedRef.current = true;
    refreshWalkingTourStopMap();
  }, [hasTourActivities, refreshWalkingTourStopMap]);

  return {
    // Core state
    trip,
    tripDays,
    loading,
    error,
    isNewTrip,

    // Edit mode
    isEditMode,
    setIsEditMode,
    hasUnsavedChanges,
    unsavedSettings,
    // Ops not yet answered (tests; the planner saves them as made).
    pendingOps,
    isSaving,

    // Form state
    tripName, setTripName,
    tripLocation, setTripLocation,
    startDate, setStartDate,
    endDate, setEndDate,
    tripType, setTripType,
    description, setDescription,
    selectedFriends, setSelectedFriends,
    destinations, setDestinations,
    itineraryViewPreference, setItineraryViewPreference,

    // Permissions
    canEdit,
    currentUserRole,
    tripOwnerId,
    isTripMember,
    isChildTraveler,
    accessNotice,
    clearAccessNotice,

    // Derived
    tripDuration,

    // Trip board
    tripBoard,
    boardCollections,
    refetchTripBoard: fetchTripBoard,
    isCreatingBoard,
    handleCreateTripBoard,

    // Walking tours
    walkingTourStopMap,
    refreshWalkingTourStopMap,

    // Transportation segments
    transports,
    upsertTransport,
    removeTransport,
    autosaveFailure,
    clearAutosaveFailure,

    // Co-editing (ops mode): notices for the banner slot and their actions
    opsMode,
    unscheduled: opsMode ? unscheduled : [],
    scheduleUnscheduled,
    removeUnscheduled,
    coeditNotices,
    dismissCoeditNotice,
    keepTheirChange,
    restoreWithChanges,
    // Stage E: undo / redo your newest change ({ redo })
    undoChange,
    // The planner's Discard: drop the edits not sent yet (Stage F)
    discardPendingOps,

    // Actions
    handleSave,
    reloadTrip,
    updateTripDay,
    addVariantForDate,
    addActivityToDay,
    addActivitiesToDay,
    removeActivitiesFromDay,
    removeActivityFromDay,
    updateActivityInDay,
    editTripDays,
    reorderActivityInDay,
    moveActivityBetweenDays,
    shiftAllDates,
    addDayAtStart,
    addDayAtEnd,
    reorderDays,
    insertDayAfter,
    deleteDayByDate,
    setDaysAndRange
  };
}

/**
 * Local tripDays plus the named server stops (`activityIds`) they don't
 * already hold, each appended to the local day of the same date (else the
 * same day id). Everything else local wins untouched. Used when a reload
 * lands on an itinerary with unsaved edits — see reloadTrip for why only
 * named stops.
 */
const stopKey = (a) => a?.activityId || a?.activity_id || a?.id || null;

// What a stop's source decides: everything _transformActivityBulk's place and
// custom branches set apart from the shared base fields. Copied as a set —
// a field the server's copy lacks is dropped, so a custom-shaped stop that
// became a place loses isCustom/source 'custom' and can't autosave Google's
// name as its own title.
const PLACE_DISPLAY_FIELDS = [
  'title', 'name', 'location', 'description', 'coordinates', 'category',
  'rating', 'userRatingsTotal', 'lat', 'lng', 'dataSource', 'ratingSource',
  'source', 'isCustom'
];

/**
 * Local tripDays with the stops at `placeIds` taking the server copy's
 * display fields (matched by activity id); their schedule, notes and every
 * other local edit stay. Used by reloadTrip when it lands on unsaved edits
 * after the server named those places.
 */
export function applyRefreshedPlaces(localDays, serverDays, placeIds = []) {
  const wanted = new Set((placeIds || []).filter(Boolean));
  if (wanted.size === 0) return localDays;
  const serverStops = new Map();
  (serverDays || []).forEach(d => (d.activities || []).forEach(a => {
    const k = stopKey(a);
    if (k) serverStops.set(k, a);
  }));
  let changed = false;
  const next = (localDays || []).map(day => {
    let dayChanged = false;
    const activities = (day.activities || []).map(a => {
      const server = serverStops.get(stopKey(a));
      if (!server || !wanted.has(a.placeId || a.place_id)) return a;
      const patched = { ...a };
      PLACE_DISPLAY_FIELDS.forEach(field => {
        if (field in server) patched[field] = server[field];
        else delete patched[field];
      });
      dayChanged = true;
      return patched;
    });
    if (!dayChanged) return day;
    changed = true;
    return { ...day, activities };
  });
  return changed ? next : localDays;
}

export function mergeServerStops(localDays, serverDays, activityIds = [], placed = null) {
  const wanted = new Set((activityIds || []).filter(Boolean));
  if (wanted.size === 0) return localDays;
  const known = new Set();
  (localDays || []).forEach(d => (d.activities || []).forEach(a => {
    const k = stopKey(a);
    if (k) known.add(k);
    // A stop added here and stored under the id it minted is already shown.
    if (a?.clientActivityId) known.add(a.clientActivityId);
  }));
  const next = (localDays || []).map(day => ({ day, added: [] }));
  let changed = false;
  (serverDays || []).forEach(serverDay => {
    (serverDay.activities || []).forEach(a => {
      const k = stopKey(a);
      if (!k || !wanted.has(k) || known.has(k)) return;
      // Date first: the composer created the stop on the date it read from
      // the local days, which may differ from the server's (unsaved date
      // edits keep day ids). Same plan variant first: a co-editor's stop on
      // a date's variant B belongs on B here, not on A.
      const variant = serverDay.plan_variant || 'A';
      const target = next.find(n => serverDay.date && n.day.date === serverDay.date && (n.day.plan_variant || 'A') === variant)
        || next.find(n => serverDay.date && n.day.date === serverDay.date)
        || next.find(n => serverDay.id != null && n.day.id === serverDay.id);
      if (!target) return;
      target.added.push(a);
      known.add(k);
      if (placed) placed.add(k);
      changed = true;
    });
  });
  if (!changed) return localDays;
  return next.map(({ day, added }) => (added.length === 0
    ? day
    : { ...day, activities: [...(day.activities || []), ...added] }));
}

export default useTripState;
